import clsx from 'clsx';
import { Fragment, useMemo } from 'react';

import {
  formatKeyboardShortcut,
  getKeyboardShortcutKeyTokens,
} from '../../../lib/keyboardShortcuts';
import { detectOS } from '../../../hooks/useHotkeys';
import type { ShortcutBinding } from '../../../lib/keyboardShortcuts/types';

interface KeyboardShortcutKeysProps {
  binding: ShortcutBinding;
  className?: string;
  size?: 'sm' | 'md';
}

const KEY_CAP_CLASS =
  'inline-flex min-h-[1.75rem] min-w-[1.75rem] items-center justify-center rounded-md border border-gray-medium bg-gray-light px-2 font-medium text-blue-dark shadow-sm';

const KEY_CAP_SIZE: Record<NonNullable<KeyboardShortcutKeysProps['size']>, string> = {
  md: `${KEY_CAP_CLASS} text-sm`,
  sm: `${KEY_CAP_CLASS} text-xs`,
};

/**
 * Renders a shortcut binding as individual key caps (readable in help modals and menus).
 */
export default function KeyboardShortcutKeys({
  binding,
  className,
  size = 'md',
}: KeyboardShortcutKeysProps) {
  const os = useMemo(() => (detectOS() === 'mac' ? 'mac' : 'windows'), []);
  const keyGroups = useMemo(() => getKeyboardShortcutKeyTokens(binding, os), [binding, os]);
  const accessibleLabel = useMemo(() => formatKeyboardShortcut(binding, os), [binding, os]);

  return (
    <span
      aria-label={accessibleLabel}
      className={clsx('inline-flex flex-wrap items-center justify-end gap-1.5', className)}
      role="group"
    >
      {keyGroups.map((keys, groupIndex) => (
        <Fragment key={groupIndex}>
          {groupIndex > 0 && (
            <span aria-hidden className="text-gray-dark px-0.5 text-sm font-semibold">
              +
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            {keys.map((keyLabel, keyIndex) => (
              <Fragment key={`${groupIndex}-${keyIndex}`}>
                {keyIndex > 0 && os === 'windows' && (
                  <span aria-hidden className="text-gray-dark text-xs font-medium">
                    +
                  </span>
                )}
                <kbd className={KEY_CAP_SIZE[size]}>{keyLabel}</kbd>
              </Fragment>
            ))}
          </span>
        </Fragment>
      ))}
    </span>
  );
}
